import React, { useState, useEffect } from 'react';
import { Sun, Heart, Moon, Compass, Feather, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const iconMap = {
  Sun: Sun,
  Moon: Moon,
  Eye: Eye,
  Feather: Feather,
  Heart: Heart,
  Compass: Compass
};

// eslint-disable-next-line no-unused-vars
const experiencesData = [
  {
    id: 'yoga',
    title: 'Yoga & Breathwork',
    philosophy: 'Yogas Chitta Vritti Nirodhah',
    philosophyTranslation: 'Yoga is the cessation of the fluctuations of the mind.',
    icon: Sun,
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=800',
    description: 'Born on the banks of the sacred Saraswati river thousands of years ago, Yoga is far more than physical postures (Asanas). It is a comprehensive system of liberation designed to prepare the body for absolute stillness and meditative absorption through posture, breath control (Pranayama), and withdrawal of the senses.',
    teachings: [
      'Asana (Physical postures to release energetic blocks)',
      'Pranayama (Conscious regulation of vital life force)',
      'Pratyahara (Sensory withdrawal to quiet external stimuli)',
      'Dharana (Deep, single-pointed concentration)'
    ],
    centers: 'Parmarth Niketan (Rishikesh), Isha Yoga Center (Coimbatore), Bihar School of Yoga (Munger)',
    duration: 'Typically 7 to 28 days for full immersive programs'
  },
  {
    id: 'meditation',
    title: 'Silent Meditation',
    philosophy: 'Appo Deepo Bhava',
    philosophyTranslation: 'Be a light unto yourself.',
    icon: Moon,
    image: 'https://images.unsplash.com/photo-1447452001602-7090c7ab2db3?q=80&w=800',
    description: 'India is the womb of both Buddhist Vipassana and Vedic Dhyana. Silent meditation practices here involve withdrawing into ashrams, caves, or high Himalayan valleys to observe the mind. By remaining in noble silence (Mauna), seekers peel back layers of mental conditioning to experience the true nature of reality.',
    teachings: [
      'Vipassana (Insight meditation into impermanence)',
      'Dhyana (Sustained flow of meditative awareness)',
      'Mauna (Noble silence to conserve speech energy)',
      'Mantra Japa (Repetition of sacred sounds to anchor the mind)'
    ],
    centers: 'Dhamma Giri (Igatpuri), Tushita Meditation Centre (Dharamshala), Osho Meditation Resort (Pune)',
    duration: '10-day silent retreats are standard for beginners'
  },
  {
    id: 'temples',
    title: 'Sacred Temples & Geometry',
    philosophy: 'Aham Brahmasmi',
    philosophyTranslation: 'I am the absolute reality.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1544085311-11a028465b03?q=80&w=800',
    description: 'Ancient Indian temples are not merely places of worship; they are highly sophisticated energy structures built according to Vastu Shastra (sacred geometry). Constructed at specific telluric energy junctions, their architectural layout, stone composition, and sonic vibrations created during daily pujas act as battery chargers for the human nervous system.',
    teachings: [
      'Agama Shastras (Cosmic science of temple design)',
      'Sanskrit Chanting (Sonic resonance to balance chakras)',
      'Pradakshina (Circumambulation to absorb structural energy)',
      'Darshan (Devotional connection with the deity portal)'
    ],
    centers: 'Meenakshi Temple (Madurai), Kashi Vishwanath (Varanasi), Brihadeeswarar Temple (Thanjavur)',
    duration: '2 to 5 days cultural and architectural journeys'
  },
  {
    id: 'churches',
    title: 'Historic Churches & Sanctuaries',
    philosophy: 'Gloria in Excelsis Deo',
    philosophyTranslation: 'Glory to God in the highest.',
    icon: Feather,
    image: 'https://images.unsplash.com/photo-1548013146-72479768bada?q=80&w=800',
    description: 'India\'s Christian heritage is incredibly ancient, beginning in 52 AD when St. Thomas the Apostle landed on the coast of Kerala. From the Portuguese-colonial white basilicas of Goa to the ancient Syrian Malabar shrines of Kerala, these holy sites represent a distinct thread of contemplative peace, silent adoration, and historic sanctuary.',
    teachings: [
      'Silent Adoration (Contemplative prayer in ancient sanctuaries)',
      'Gothic & Baroque Sacred Art (Visual meditation portals)',
      'Saintly Pilgrimages (Seeking grace and healing through relics)',
      'Interfaith Harmony (Unifying Christian devotion with Indian mysticism)'
    ],
    centers: 'Basilica of Bom Jesus (Goa), St. Thomas Syro-Malabar Church (Palayur), Malayattoor Kurisumudy (Kerala)',
    duration: '1 to 3 days historical spiritual exploration'
  },
  {
    id: 'ayurveda',
    title: 'Ayurvedic Healing',
    philosophy: 'Swasthasya Swasthya Rakshanam',
    philosophyTranslation: 'To protect the health of the healthy and heal the sick.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=800',
    description: 'Ayurveda is India\'s 5,000-year-old system of preventative healthcare and longevity. It views health as a state of complete physical, mental, and spiritual equilibrium. Therapies use botanical extractions, deep massage, steam boxes, and tailored diets to remove toxins and realign your primary energetic bio-elements (Doshas).',
    teachings: [
      'Panchakarma (Five purification therapies to cleanse body tissue)',
      'Dinacharya (Daily self-care routine aligned with nature\'s cycles)',
      'Ahara (Food as medicine customized to body types)',
      'Rasayana (Rejuvenation therapies for cellular longevity)'
    ],
    centers: 'Somatheeram Ayurvedic Beach Resort (Kerala), Ananda in the Himalayas (Rishikesh)',
    duration: '7, 14, or 21 days for therapeutic clinical detox'
  },
  {
    id: 'journeys',
    title: 'Sacred Pilgrimages (Yatra)',
    philosophy: 'Charaiveti, Charaiveti',
    philosophyTranslation: 'Keep moving, keep moving forward.',
    icon: Compass,
    image: 'https://images.unsplash.com/photo-1506461883276-594a12b11db3?q=80&w=800',
    description: 'In Bharat, a spiritual journey is called a Yatra. It is a deliberate, difficult physical pilgrimage undertaken to cross over from the material to the spiritual realm. Whether trekking through rugged Himalayan passes to Char Dham or tracing the footsteps of the Buddha along the Gangetic plains, a Yatra is a crucible that burns away karma.',
    teachings: [
      'Char Dham Yatra (The four high-altitude mountain sanctuaries)',
      'Buddhist Circuit (Tracing the steps of Gautama Buddha\'s enlightenment)',
      'Narmada Parikrama (The holy walk along the Narmada River banks)',
      'Kailash Mansarovar (The ultimate cosmic mountain walk)'
    ],
    centers: 'Char Dham (Himalayas), Buddhist Circuit (Bodh Gaya to Sarnath), Amarnath Cave (Kashmir)',
    duration: '10 to 25 days depending on the route and terrain difficulty'
  }
];

export default function Experiences() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(null);
  const [experiences, setExperiences] = useState([]);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const res = await fetch(`${apiBase}/api/experiences`);
        if (res.ok) {
          const data = await res.json();
          const dbExperiences = data.map(e => ({
            id: e.id.toString(),
            title: e.title,
            philosophy: e.philosophy,
            philosophyTranslation: e.philosophyTranslation,
            icon: iconMap[e.icon] || Compass,
            image: e.image,
            description: e.description,
            teachings: e.teachings || [],
            centers: e.centers,
            duration: e.duration
          }));

          setExperiences(dbExperiences);
          if (dbExperiences.length > 0) {
            setActiveTab(dbExperiences[0].id);
          }
        }
      } catch (err) {
        console.error("Error fetching experiences:", err);
      }
    };
    fetchExperiences();
  }, [apiBase]);

  const currentExp = experiences.find(e => e.id === activeTab) || experiences[0];
  const IconComponent = currentExp ? currentExp.icon : Compass;

  if (experiences.length === 0) {
    return (
      <section style={{ padding: '100px 0', backgroundColor: 'var(--bg-deep)', textAlign: 'center' }}>
        <h2 className="font-serif text-gold">Loading Experiences...</h2>
      </section>
    );
  }

  return (
    <section
      id="experiences"
      style={{
        padding: '100px 0',
        backgroundColor: 'var(--bg-deep)',
        position: 'relative'
      }}
    >
      {/* Decorative Gold Elements */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.03) 0%, transparent 70%)',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />

      <div
        style={{
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <span
            className="text-saffron"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}
          >
            {t('experiences.header')}
          </span>
          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              marginTop: '10px',
              color: 'var(--text-light)'
            }}
          >
            {t('experiences.title')}
          </h2>
          <div
            style={{
              width: '60px',
              height: '2px',
              backgroundColor: 'var(--primary-saffron)',
              margin: '20px auto 0'
            }}
          />
        </div>

        {/* Outer Split Container */}
        <div
          className="flex-split-container"
          style={{
            display: 'flex',
            gap: '40px',
            marginTop: '20px'
          }}
        >
          {/* Left Navigation: Vertical tabs */}
          <div
            style={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            {experiences.map((exp) => {
              const ExpIcon = exp.icon || Compass;
              return (
                <button
                  key={exp.id}
                  onClick={() => setActiveTab(exp.id)}
                  className={`glass-card tab-button ${activeTab === exp.id ? 'active-tab' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '22px 30px',
                    textAlign: 'left',
                    width: '100%',
                    cursor: 'pointer',
                    borderRadius: '8px'
                  }}
                >
                  <div
                    className="tab-icon-container"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'var(--transition-fast)'
                    }}
                  >
                    <ExpIcon size={24} strokeWidth={1.5} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                    <span
                      className="font-serif tab-title-text"
                      style={{
                        fontSize: '1.2rem',
                        transition: 'var(--transition-fast)',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {t(`experiences.items.${exp.id}.title`, { defaultValue: exp.title })}
                    </span>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginTop: '3px',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        opacity: 0.8
                      }}
                    >
                      {t(`experiences.items.${exp.id}.philosophy`, { defaultValue: exp.philosophy })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Display Panel */}
          <div
            className="glass-card display-panel"
            style={{
              flex: '1.8',
              padding: '40px',
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              animation: 'fadeIn 0.5s ease-out'
            }}
            key={activeTab} // Forces animation when tab changes
          >
            {/* Background image preview with smooth gradient fade */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40%',
                height: '100%',
                backgroundImage: `linear-gradient(to right, var(--bg-panel) 0%, rgba(234, 224, 204, 0.8) 100%), url(${currentExp.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.15,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />

            <div style={{ position: 'relative', zIndex: 2 }}>
              {/* Header Icon + Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
                <div style={{ color: 'var(--primary-gold)', border: '1px solid var(--primary-gold-border)', borderRadius: '50%', padding: '10px' }}>
                  <IconComponent size={30} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-gradient" style={{ fontSize: '2rem' }}>{t(`experiences.items.${currentExp.id}.title`, { defaultValue: currentExp.title })}</h3>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
                    {t('experiences.philosophyLabel')}: <strong style={{ color: 'var(--primary-saffron)', fontWeight: '500' }}>{t(`experiences.items.${currentExp.id}.philosophy`, { defaultValue: currentExp.philosophy })}</strong>
                  </span>
                </div>
              </div>

              {/* Wisdom quote card */}
              <blockquote className="wisdom-quote">
                "{t(`experiences.items.${currentExp.id}.philosophyTranslation`, { defaultValue: currentExp.philosophyTranslation })}"
              </blockquote>

              {/* Core description */}
              <p style={{ fontSize: '0.92rem', color: 'var(--text-muted)', lineHeight: '1.7', marginBottom: '30px' }}>
                {t(`experiences.items.${currentExp.id}.description`, { defaultValue: currentExp.description })}
              </p>

              {/* Core Teachings */}
              <div style={{ marginBottom: '30px' }}>
                <h4 className="font-serif text-gold" style={{ fontSize: '1.15rem', marginBottom: '15px' }}>{t('experiences.corePillars')}</h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '12px'
                  }}
                >
                  {t(`experiences.items.${currentExp.id}.teachings`, { returnObjects: true, defaultValue: currentExp.teachings }).map((teach, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      <span className="text-saffron">•</span>
                      <span>{teach}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom details banner */}
            <div
              style={{
                position: 'relative',
                zIndex: 2,
                marginTop: '20px',
                borderTop: '1px solid rgba(212, 175, 55, 0.15)',
                paddingTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '15px'
              }}
            >
              <div>
                <span style={{ display: 'block', fontSize: '0.7rem', uppercase: true, letterSpacing: '0.1em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  {t('experiences.signatureCenters')}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                  {t(`experiences.items.${currentExp.id}.centers`, { defaultValue: currentExp.centers })}
                </span>
              </div>
              <div>
                <span style={{ display: 'block', fontSize: '0.7rem', uppercase: true, letterSpacing: '0.1em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  {t('experiences.recommendedTime')}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--primary-saffron)' }}>
                  {t(`experiences.items.${currentExp.id}.duration`, { defaultValue: currentExp.duration })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tab-button {
          background: rgba(255, 255, 255, 0.45) !important;
          border: 1px solid rgba(181, 137, 37, 0.1) !important;
          border-left: 4px solid transparent !important;
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }
        .tab-button .tab-icon-container {
          color: var(--text-muted);
        }
        .tab-button .tab-title-text {
          color: var(--text-light);
        }
        .tab-button:hover {
          background: rgba(212, 175, 55, 0.06) !important;
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateX(4px);
        }
        .active-tab {
          background: linear-gradient(90deg, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.02) 100%) !important;
          border-color: rgba(212, 175, 55, 0.4) !important;
          border-left-color: var(--primary-gold) !important;
          box-shadow: 0 10px 30px rgba(181, 137, 37, 0.08) !important;
        }
        .active-tab .tab-icon-container {
          color: var(--primary-gold) !important;
          transform: scale(1.1);
        }
        .active-tab .tab-title-text {
          color: var(--primary-gold) !important;
        }
        .wisdom-quote {
          border-left: 3px solid var(--primary-saffron) !important;
          background: rgba(194, 94, 26, 0.04);
          padding: 20px 25px !important;
          border-radius: 0 8px 8px 0;
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.05rem !important;
          color: var(--text-light);
          margin-bottom: 25px;
          line-height: 1.6;
        }
        
        @media (max-width: 900px) {
          .flex-split-container {
            flex-direction: column !important;
          }
          .display-panel {
            padding: 30px 20px !important;
          }
        }
      `}</style>
    </section>
  );
}
